import { z } from 'zod';
import { DeviceStatus } from './types';

const itemSchema = z.object({
	value: z.string(),
	label: z.string()
});

export const landownerSchema = z.object({
	firstName: z.string().nonempty({ message: 'First name is required' }),
	lastName: z.string().nonempty({ message: 'Last name is required' }),
	streetAddress: z.string().nonempty({ message: 'Street address is required' }),
	city: z.string().nonempty({ message: 'City is required' }),
	state: z.string().nonempty({ message: 'State is required' }),
	zip: z.string().nonempty({ message: 'Zip code is required' }),
	mailingAddress: z
		.string()
		.optional()
		.transform(s => s ?? ''),
	email: z
		.string()
		.email({ message: 'Invalid email address' })
		.optional()
		.or(z.literal('')),
	phone: z.string().nonempty({ message: 'Phone number is required' }),
	mobile: z
		.string()
		.optional()
		.transform(s => s ?? ''),
	site: z.array(itemSchema).nonempty('At least one site is required'),
	initialPayment: z.coerce
		.number({ message: 'Initial payment is required' })
		.nonnegative({ message: 'Initial payment can not be negative' }),
	recurringPayment: z.coerce
		.number({ message: 'Recurring payment is required' })
		.nonnegative({ message: 'Recurring payment can not be negative' }),
	initialDeploymentDate: z
		.union([
			z
				.date({ required_error: 'Initial deployment date is required' })
				.max(new Date(), { message: "Initial deployment date can't be in the future" }),
			z.null()
		])
		.refine(date => date instanceof Date && !isNaN(date.getTime()), {
			message: 'Initial deployment date is required'
		}),
	status: z.nativeEnum(DeviceStatus, {
		message: 'Status is required'
	}),
	latitude: z.coerce
		.number({ message: 'Latitude is required' })
		.min(-90, { message: 'Latitude must be greater than or equal to -90' })
		.max(90, { message: 'Latitude must be less than or equal to 90' }),
	longitude: z.coerce
		.number({ message: 'Longitude is required' })
		.min(-180, { message: 'Longitude must be greater than or equal to -180' })
		.max(180, { message: 'Longitude must be less than or equal to 180' })
});

export type LandOwnerFormSchema = z.infer<typeof landownerSchema>;
